import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  Link,
  TypographyProps,
  Menu,
  MenuItem,
  IconButton,
} from '@mui/material';
import kebabCase from 'lodash/kebabCase';
import {useState, useMemo, useEffect} from 'react';
import {Image, ImageProps} from '@jobseeker/lotus/components/Image';
import type {JobListProps} from '../type';
import {
  YellowCrown,
  DefaultJobLogo,
  Dot,
  ThreeDotMenuIcon,
  CheckCircle,
  StarIconV2,
  GreyDivider,
} from '@jobseeker/lotus/icons';
import {getRandomColor} from '@jobseeker/lotus/theme';
import JobTag from './JobTags';
import {dateToDuration} from '@jobseeker/lotus/utils/date';
import NextLink from 'next/link';
import {FaStar, FaRegStar} from 'react-icons/fa';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {Tooltip} from '@jobseeker/lotus/elements';
import {customKebab} from '@jobseeker/lotus/utils';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
  brandedJD?: boolean;
}
export const ImageWithFallback = ({
  fallback,
  alt,
  src,
  brandedJD = false,
  ...props
}: ImageWithFallbackProps) => {
  const [error, setError] = useState(false);
  const theme = useTheme();
  const isLarge = useIsLargeDevice();

  useEffect(() => {
    setError(false);
  }, [src]);

  const randomColor = useMemo(() => getRandomColor(props.itemId), [src]);
  return (
    <>
      {!error ? (
        <Image
          key={alt}
          src={src}
          alt={alt}
          loading="lazy"
          onErrorCapture={() => setError(true)}
          {...props}
          style={{
            objectFit: 'contain',
            padding: brandedJD ? (isLarge ? '0px' : '2px') : '2px',
            marginTop: brandedJD ? (isLarge ? '-35px' : '0px') : '0',
            backgroundColor: 'white',
          }}
        />
      ) : (
        <Box
          key={`${alt}_${randomColor}`}
          className="joblist__logo"
          sx={{
            background: randomColor,
            p: theme.spacing(1.5),
            height: 'min-content',
            lineHeight: 0,
          }}
        >
          <Image
            src={DefaultJobLogo}
            alt={'Logo'}
            width={'25'}
            height={'25'}
            objectFit="cover"
            quality={50}
          />
        </Box>
      )}
    </>
  );
};

export const Typography = styled(Typo)<any>((props: TypographyProps) => ({
  display: 'flex',
  alignItems: 'center',
}));

export const JoblistPaper = styled(Paper)<any>((props: any) => ({
  position: 'relative',
  display: 'flex',
  gap: props.theme.spacing(1.5),
  // cursor: 'pointer',
  '.joblist__logo': {
    borderRadius: props.theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: props.theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    // padding: props.theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: props.theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
  borderRadius: props?.brandedJD ? '24px' : 0,
}));

const JobListComponent = (props: JobListProps) => {
  const theme = useTheme();
  const {
    date,
    jobData,
    isDetailCard,
    showSaveIcon = true,
    showThreeDotMenu = false,
    showPosted = true,
    redirectionLink = '',
    showApplied = false,
    whatFor = '',
    jobPos,
  } = props;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  useEffect(() => {
    const handler = () => {
      setOpen(false);
    };
    window?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  const dateString = useMemo(() => {
    return dateToDuration(date, true);
  }, [date]);

  const ambitionBoxInfo = props.jobData?.companyData?.ambitionBoxInfo;
  const showcasePage = props.jobData?.showcase;
  const createdTimeMs = props.jobData?.createdTimeMs;

  const rating = ambitionBoxInfo?.aggregateRatingOneDecimal;
  const reviewsCount = ambitionBoxInfo?.reviewsCount;

  const isRatingValid =
    typeof rating === 'number' && rating >= (props?.jobData?.minRatingAb || 0);
  const hasReviews = typeof reviewsCount === 'number' && reviewsCount > 0;
  const isNotShowcase = !showcasePage;
  const isActiveJob =
    typeof createdTimeMs === 'number' &&
    Date.now() - createdTimeMs <= 180 * 24 * 60 * 60 * 1000;

  const showRating =
    ambitionBoxInfo &&
    isRatingValid &&
    hasReviews &&
    isNotShowcase &&
    isActiveJob;

  const savedDateString = useMemo(() => {
    return dateToDuration(props.jobData?.savedDate as string, true);
  }, [props.jobData?.savedDate]);

  const randomColor = useMemo(
    () => getRandomColor(props.jobData?.id),
    [props.companyImgUrl, props.jobData?.companyData]
  );
  const MainComponent = isDetailCard ? Card : Box;

  const onJobClick = (event: any) => {
    // alert('test');
    event?.stopPropagation();
    if (props.onJobClick) {
      props.onJobClick(props.jobData);
    }
  };

  const onSaveClick = (event: any) => {
    event?.preventDefault();
    event?.stopPropagation();
    // setSaved(!saved);
    if (props.onSaveClick) {
      props.onSaveClick(props.jobData, !!props.jobData?.saved);
    }
  };
  const isLarge = useIsLargeDevice();

  const imageLoader = ({src}: {src: string}) => {
    return props.companyImgUrl ?? '';
  };

  const title = customKebab(props?.jobData?.title);
  let href = `/j/${title}-${props?.jobData?.id}?ref=${props?.refCode}&jobPos=${jobPos}`;

  if (redirectionLink.length > 0) {
    href = redirectionLink;
  }

  return (
    <MainComponent
      className="joblist-card-v2"
      color="gray.dark"
      onClick={!isDetailCard ? onJobClick : undefined}
      sx={{
        position: 'relative',
        transition: 'all ease 0.2s',
        cursor: isDetailCard ? 'auto' : 'pointer',
        borderBottom: 'solid 1px',
        borderColor: isDetailCard ? '#fff' : '#7E7E7E33',
        ':hover': isDetailCard
          ? {}
          : {
              boxShadow: '6px 6px 14px 3px rgba(19, 21, 23, 0.04)',
              zIndex: 2,
              borderColor: '#fff',
            },
      }}
    >
      <a
        href={href}
        target="_blank"
        onClick={e => e.stopPropagation()}
        style={{
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <JoblistPaper
          elevation={0}
          sx={{
            ...props.sx,
            py: {xxs: isDetailCard ? 3 : 1.5, md: isDetailCard ? 3 : 2},
            px: {xxs: 1.5, md: 2},
          }}
        >
          <Box
            sx={{
              display: 'flex',
            }}
          >
            {/* {props.companyImgUrl ? ( */}
            {(whatFor === 'course' && props.companyImgUrl?.length) ||
            (!props.jobData?.confidential &&
              props.jobData?.companyData?.companyLogoFilePath) ? (
              <ImageWithFallback
                className="joblist__logo"
                // src={props.companyImgUrl ?? ''}
                // fallback={props.fallbackCompanyUrl}
                // blurDataURL={props.companyImgUrl}
                // loader={imageLoader}

                src={
                  props.jobData?.companyData?.companyLogoFilePath ??
                  (whatFor === 'course' ? (props?.companyImgUrl ?? '') : '')
                }
                fallback={props.fallbackCompanyUrl}
                blurDataURL={
                  props.jobData?.companyData?.companyLogoFilePath ||
                  props.companyImgUrl ||
                  ''
                }
                // loader={imageLoader}

                alt={`logo_${jobData?.id}`}
                width="50"
                height={'50'}
                objectFit="cover"
                itemId={jobData?.id}
                quality={50}
                placeholder="blur"
              />
            ) : (
              <Box
                className="joblist__logo"
                sx={{
                  background: randomColor,
                  p: theme.spacing(1.5),
                  height: 'min-content',
                  lineHeight: 0,
                }}
              >
                <Image
                  src={DefaultJobLogo}
                  alt={'Logo'}
                  width="25"
                  height={'25'}
                  objectFit="cover"
                  quality={50}
                />
              </Box>
            )}
          </Box>
          <Box
            sx={{
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
              width: '100%',
            }}
          >
            <Box
              // sx={{pr: {xxs: 3, sm: 12.2}}}
              display={'flex'}
              justifyContent={'space-between'}
              gap={2}
              alignItems={'flex-start'}
            >
              <Typography
                component={'p'}
                data-testid="job_title"
                variant="subtitle2"
                fontSize={{xxs: 14}}
                lineHeight="21px"
                fontWeight={500}
                width={'90%'}
                sx={
                  isDetailCard
                    ? {}
                    : {
                        lineClamp: '3',
                        '-webkit-line-clamp': '2',
                        '-webkit-box-orient': 'vertical',
                        // display: '-webkit-flex',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        wordWrap: 'break-word',
                        display: 'block',
                        alignItems: 'center',
                      }
                }
              >
                {props.title}
                {isLarge && showSaveIcon && (
                  <Tooltip
                    id="save_job"
                    data-testid="save_job"
                    title={props.isSaved ? 'Saved' : 'Save Job'}
                    arrow
                  >
                    <Box
                      component="span"
                      sx={{
                        color: 'primary.main',
                        position: 'relative',
                        top: '1px', 
                        ml: 0.5,
                        display: 'inline-block',
                      }}
                      className="save-icon"
                      onClick={onSaveClick}
                    >
                      {props.isSaved ? <FaStar /> : <FaRegStar />}
                    </Box>
                  </Tooltip>
                )}
              </Typography>

              {props.isPremiumJob && (
                <Box
                  sx={{
                    position: 'absolute',
                    right: {
                      xxs: showThreeDotMenu ? 12 : 12,
                      sm: showThreeDotMenu ? 36 : 16,
                    },
                    top: {
                      xxs: showThreeDotMenu ? 12 : 11,
                      sm: showThreeDotMenu ? 12 : 16,
                    },
                    width: 16,
                    height: 16,
                  }}
                >
                  <Image
                    className="joblist__premium_icon"
                    src={YellowCrown}
                    alt={'premium_icon'}
                    width={isLarge ? 16 : 14}
                    height={isLarge ? 16 : 14}
                    objectFit="cover"
                    quality={50}
                  />
                </Box>
              )}

              {showThreeDotMenu && (
                <Box
                  sx={{
                    width: 16,
                    height: 16,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'absolute',
                    right: {xxs: 11, sm: 12},
                    bottom: {xxs: 15, sm: 53},
                  }}
                >
                  <>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleClose}
                      disableScrollLock={true}
                      PaperProps={{
                        sx: {
                          border: '1px solid #7E7E7E33',
                          borderRadius: '16px',
                          marginTop: '14px',
                          height: '76px',
                        },
                      }}
                      transformOrigin={{horizontal: 'right', vertical: 'top'}}
                      anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
                    >
                      <MenuItem
                        style={{backgroundColor: 'transparent'}}
                        onClick={handleClose}
                      >
                        <Box
                          display={'flex'}
                          flexDirection={'column'}
                          onClick={onSaveClick}
                        >
                          <Box
                            display={'flex'}
                            justifyContent={'flex-start'}
                            alignItems={'center'}
                          >
                            <Box
                              display={'flex'}
                              justifyContent={'center'}
                              component={'span'}
                              sx={{color: 'primary.main'}}
                              className="save-icon"
                              mr={1}
                            >
                              {<FaStar />}
                            </Box>
                            <Typography
                              component={'h1'}
                              fontSize={14}
                              fontWeight={500}
                              display="flex"
                              color="secondary.dark"
                            >
                              Unsave
                            </Typography>
                          </Box>
                          <Typography
                            component={'h1'}
                            fontSize={10}
                            fontWeight={400}
                            display="flex"
                            mt={'10px'}
                            color="text.secondary"
                          >
                            Saved {savedDateString}
                          </Typography>
                        </Box>
                      </MenuItem>
                    </Menu>
                    <IconButton size="medium" onClick={handleClick}>
                      <Image
                        src={ThreeDotMenuIcon}
                        alt={'three_dot_icon'}
                        width={12}
                        height={12}
                        objectFit="cover"
                        quality={50}
                      />
                    </IconButton>
                  </>
                </Box>
              )}
            </Box>
            <Box
              className="joblist__subtitle"
              sx={{
                display: 'flex',
                columnGap: 1,
                rowGap: 0.5,
                alignItems: 'center',
                justifyContent: 'space-between',
                color: theme.palette.text.secondary,
                flexWrap: 'nowrap',
                mb: '-2px',
                mt: '8px',
              }}
            >
              <Box
                display={'flex'}
                gap={1}
                flexDirection={{xxs: 'column', md: 'row'}}
              >
                <Box
                  sx={{display: 'flex', alignItems: 'center'}}
                  flexWrap={'wrap'}
                >
                  <Typography
                    component={'span'}
                    data-testid="job_experience"
                    sx={{lineHeight: '16px'}}
                    variant="subtitle3"
                  >
                    {props.experiance}
                  </Typography>
                  <Image
                    src={Dot}
                    alt="."
                    width={4}
                    height={4}
                    style={{margin: '0 8px'}}
                  />
                  <Typography
                    sx={{
                      // maxWidth: '100%',
                      overflow: 'hidden',
                      position: 'relative',
                      display: 'inline-block',
                      textOverflow: 'ellipsis',
                      lineHeight: '16px',
                      // whiteSpace: 'nowrap',
                    }}
                    component={'p'}
                    data-testid="job_location"
                    variant="subtitle3"
                  >
                    {props.location}
                  </Typography>
                  {showRating && !props.jobData?.confidential && (
                    <Box
                      display={{xxs: 'none', md: 'flex'}}
                      alignItems={'center'}
                    >
                      <Image
                        src={Dot}
                        alt="."
                        width={4}
                        height={4}
                        style={{margin: '0 8px'}}
                      />
                      <Tooltip
                        arrow
                        title={
                          <Typography
                            variant="subtitle3"
                            lineHeight={2.25}
                            color="secondary.main"
                          >
                            Powered By AmbitionBox
                          </Typography>
                        }
                        placement="bottom-start"
                      >
                        <Box display="flex" alignItems="center">
                          <Image
                            src={StarIconV2}
                            height={12}
                            width={12}
                            alt="star-icon"
                          />

                          <Typography
                            sx={{
                              color: 'text.secondary',
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: '18px',
                              ml: '4px',
                            }}
                          >
                            {rating}
                          </Typography>

                          <Image
                            src={GreyDivider}
                            height={14}
                            width={10}
                            alt="grey-divider"
                          />
                          <Typography
                            sx={{
                              color: 'text.secondary',
                              fontFamily: 'Poppins',
                              fontSize: '12px',
                              fontStyle: 'normal',
                              fontWeight: 500,
                              lineHeight: '18px',
                            }}
                          >
                            {`${reviewsCount.toLocaleString()}+ Reviews`}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Box>
                  )}
                </Box>
                {<JobTag tags={props.additionalInfo} />}
              </Box>
              <Box display={'flex'} justifyContent={'space-between'}>
                {!!props.jobData?.applied &&
                  (showThreeDotMenu || showApplied) && (
                    <Typography
                      component={'span'}
                      variant="caption"
                      fontWeight={500}
                      color={'text.primary'}
                      sx={{
                        display: {xxs: 'none', md: 'flex'},
                      }}
                    >
                      <Image
                        src={CheckCircle}
                        alt="check"
                        width={12}
                        height={12}
                        style={{marginRight: theme.spacing(0.5)}}
                      />
                      APPLIED
                    </Typography>
                  )}

                {showPosted && (
                  <Typography
                    component={'span'}
                    color="text.secondary"
                    data-testid="date_posted"
                    variant="caption"
                    display={{xxs: 'none', md: 'flex'}}
                    minWidth={'100px'}
                    alignItems={'center'}
                    justifyContent={'flex-end'}
                    ml={1}
                  >
                    Posted {dateString}
                  </Typography>
                )}
              </Box>
            </Box>
            <Box
              display={{xxs: 'flex', md: 'none'}}
              justifyContent={'space-between'}
              alignItems={'center'}
            >
              <Box alignItems={'center'}>
                {showPosted && (
                  <Box
                    sx={{
                      width: '100%',
                      mt: 1,
                      display: {xxs: 'flex', md: 'none'},
                    }}
                  >
                    <Typography
                      component={'span'}
                      color="text.secondary"
                      variant="caption"
                    >
                      Posted {dateString}
                    </Typography>
                  </Box>
                )}
              </Box>
              {showRating && !props.jobData?.confidential && (
                <Box
                  display={'flex'}
                  alignItems={'center'}
                  marginTop={1}
                  mr={showThreeDotMenu ? 2 : 0}
                >
                  <Tooltip
                    arrow
                    title={
                      <Typography
                        variant="subtitle3"
                        lineHeight={2.25}
                        color="secondary.main"
                      >
                        Powered By AmbitionBox
                      </Typography>
                    }
                  >
                    <Box display={'flex'} alignItems="center">
                      <Box display={'flex'} marginBottom={'2px'}>
                        <Image
                          src={StarIconV2}
                          height={12}
                          width={12}
                          alt="star-icon"
                        />
                      </Box>

                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontFamily: 'Poppins',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '18px',
                          ml: '4px',
                        }}
                      >
                        {rating}
                      </Typography>
                      <Image
                        src={GreyDivider}
                        height={14}
                        width={10}
                        alt="grey-divider"
                        style={{
                          marginLeft: '1px',
                        }}
                      />
                      <Typography
                        sx={{
                          color: 'text.secondary',
                          fontFamily: 'Poppins',
                          fontSize: '10px',
                          fontStyle: 'normal',
                          fontWeight: 400,
                          lineHeight: '18px',
                        }}
                      >
                        {`${reviewsCount.toLocaleString()}+ Reviews`}
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              )}
            </Box>

            {/* {props.jobData?.showcase?.showcaseUrl && (
              <Link
                href={props.jobData?.showcase?.showcaseUrl ?? '#'}
                target="__blank"
                sx={{
                  textDecoration: 'none',
                  mt: 1.5,
                  display: {xxs: 'none', md: 'flex'},
                }}
              >
                <Typography variant="subtitle3">
                  Visit Company Profile
                </Typography>
              </Link>
            )} */}
          </Box>
        </JoblistPaper>
      </a>
      {isDetailCard && props.jobData?.tags?.length && (
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #7E7E7E33',
            display: 'flex',
            flexWrap: 'wrap',
            columnGap: 1,
          }}
        >
          {props.jobData?.tags?.map(tag => (
            <Link
              href={`/k/${kebabCase(tag.name)}-jobs`}
              key={`TAG#${tag.id}`}
              component={NextLink}
              sx={{
                textDecoration: 'none',
                color: 'text.primary',
                transition: 'all ease 0.3s',
                ':hover': {color: 'text.secondary'},
              }}
            >
              <Typography component="span" variant={'caption'} fontSize={12}>
                #{tag.name}
              </Typography>
            </Link>
          ))}
        </Box>
      )}
    </MainComponent>
  );
};

export default JobListComponent;
